import {
  Organization,
  OrgMembershipResponse,
  PersistentBrain,
  PersistentProject,
  PersistentThread,
  LakehouseMetricResponse,
  DataSourceModel,
  DashboardModel,
  CreateDashboardRequest,
} from "../sdk";
import React, { createContext, useContext, useCallback, useMemo } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  organizationsGetOrganization,
  organizationsGetOrganizationBrains,
  organizationsGetOrganizationMembers,
  organizationsGetOrganizationProjects,
  organizationsGetOrganizationThreads,
  ontologyCreateDashboard,
  ontologyDeleteDashboard,
  ontologyGetDashboards,
  ontologyGetDatasources,
  listLakehouseMetrics,
} from "../sdk";
import { queryKeys } from "../query/keys";
import { HIGH_CHURN_STALE_TIME, MEDIUM_CHURN_STALE_TIME, LOW_CHURN_STALE_TIME, GC_TIME } from "../query/config";

type OrgContextType = {
  org: Organization | null;
  threads: PersistentThread[];
  projects: PersistentProject[];
  brains: PersistentBrain[];
  metrics: LakehouseMetricResponse[];
  datasources: DataSourceModel[];
  fetchOrgData: (orgId: string) => Promise<void>;
  loading: boolean;
  isRefreshing: boolean;
  error: Error | null;
  members: OrgMembershipResponse[];
  dashboards: DashboardModel[];
  createDashboard: (
    dashboard: CreateDashboardRequest
  ) => Promise<DashboardModel>;
  deleteDashboard: (dashboardId: string) => Promise<void>;
  retry: () => void;
  isError: boolean;
};

const OrgContext = createContext<OrgContextType | undefined>(undefined);

export type OrgProviderResources = Partial<{
  threads: boolean;
  projects: boolean;
  brains: boolean;
  members: boolean;
  metrics: boolean;
  datasources: boolean;
  dashboards: boolean;
}>;

const DEFAULT_ORG_RESOURCES: Required<OrgProviderResources> = {
  threads: true,
  projects: true,
  brains: true,
  members: true,
  metrics: true,
  datasources: true,
  dashboards: true,
};

export function OrgProvider({
  children,
  orgId,
  userId,
  resources,
}: {
  children: React.ReactNode;
  orgId: string;
  userId?: string;
  resources?: OrgProviderResources;
}) {
  const queryClient = useQueryClient();
  const activeResources = useMemo(
    () => ({ ...DEFAULT_ORG_RESOURCES, ...resources }),
    [resources]
  );

  // LOW CHURN: org detail, members
  const orgQuery = useQuery({
    queryKey: queryKeys.org.detail(orgId),
    queryFn: async () => {
      const res = await organizationsGetOrganization({ path: { org_id: orgId } });
      return res.data || null;
    },
    staleTime: LOW_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!orgId,
  });

  // HIGH CHURN: threads (active collaboration)
  const threadsQuery = useQuery({
    queryKey: queryKeys.threads.list({ userId, orgId }),
    queryFn: async () => {
      const res = await organizationsGetOrganizationThreads({ path: { org_id: orgId } });
      return res.data || [];
    },
    staleTime: HIGH_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: activeResources.threads && !!orgId,
  });

  // LOW CHURN: projects, brains
  const projectsQuery = useQuery({
    queryKey: queryKeys.projects.list({ userId, orgId }),
    queryFn: async () => {
      const res = await organizationsGetOrganizationProjects({ path: { org_id: orgId } });
      return res.data || [];
    },
    staleTime: LOW_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: activeResources.projects && !!orgId,
  });

  const brainsQuery = useQuery({
    queryKey: queryKeys.brains.list({ orgId }),
    queryFn: async () => {
      const res = await organizationsGetOrganizationBrains({ path: { org_id: orgId } });
      return res.data || [];
    },
    staleTime: LOW_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: activeResources.brains && !!orgId,
  });

  const membersQuery = useQuery({
    queryKey: queryKeys.org.members(orgId),
    queryFn: async () => {
      const res = await organizationsGetOrganizationMembers({ path: { org_id: orgId } });
      return res.data?.org_members || [];
    },
    staleTime: LOW_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: activeResources.members && !!orgId,
  });

  // MEDIUM CHURN: metrics, dashboards (collaborative artifacts)
  const metricsQuery = useQuery({
    queryKey: queryKeys.metrics.list({ orgId }),
    queryFn: async () => {
      const res = await listLakehouseMetrics({ query: { org_id: orgId } });
      return res.data ?? [];
    },
    staleTime: MEDIUM_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: activeResources.metrics && !!orgId,
  });

  // LOW CHURN: datasources
  const datasourcesQuery = useQuery({
    queryKey: queryKeys.datasources.list({ orgId }),
    queryFn: async () => {
      const res = await ontologyGetDatasources({ query: { org_id: orgId } });
      return res.data || [];
    },
    staleTime: LOW_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: activeResources.datasources && !!orgId,
  });

  const dashboardsQuery = useQuery({
    queryKey: queryKeys.dashboards.list({ orgId }),
    queryFn: async () => {
      const res = await ontologyGetDashboards({ query: { org_id: orgId } });
      return res.data || [];
    },
    staleTime: MEDIUM_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: activeResources.dashboards && !!orgId,
  });

  // Awaits the refetches so callers can close UI on resolution.
  const invalidateAllOrgData = useCallback(
    async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.org.all(orgId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.threads.lists() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.brains.lists() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.projects.lists() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboards.lists() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.metrics.lists() }),
        // Use the prefix so both `datasources.list` (raw DataSourceModel)
        // and `datasources.connections` (transformed DatabaseConnection)
        // refresh together — they back different surfaces but the same
        // underlying records, so any "rebuild org data" call must clear
        // both caches or the Sources page renders stale icons.
        queryClient.invalidateQueries({ queryKey: queryKeys.datasources.all }),
      ]);
    },
    [queryClient, orgId]
  );

  const fetchOrgData = useCallback(
    async (_orgId: string) => {
      await invalidateAllOrgData();
    },
    [invalidateAllOrgData]
  );

  const retry = useCallback(() => {
    void invalidateAllOrgData();
  }, [invalidateAllOrgData]);

  // Mutations using useMutation for proper state management
  const dashboardsKey = queryKeys.dashboards.list({ orgId });

  const createDashboardMutation = useMutation({
    mutationFn: async (dashboard: CreateDashboardRequest) => {
      const response = await ontologyCreateDashboard({ body: dashboard });
      if (!response.data) throw new Error("No data returned");
      return response.data;
    },
    onSuccess: (newDashboard) => {
      queryClient.setQueryData<DashboardModel[]>(
        dashboardsKey,
        (old) => [...(old || []), newDashboard]
      );
    },
  });

  const deleteDashboardMutation = useMutation({
    mutationFn: async (dashboardId: string) => {
      await ontologyDeleteDashboard({ path: { dashboard_id: dashboardId } });
      return dashboardId;
    },
    onMutate: async (dashboardId) => {
      await queryClient.cancelQueries({ queryKey: dashboardsKey });
      const previous = queryClient.getQueryData<DashboardModel[]>(dashboardsKey);
      queryClient.setQueryData<DashboardModel[]>(
        dashboardsKey,
        (old) => (old || []).filter((d) => d.id !== dashboardId)
      );
      return { previous };
    },
    onError: (_err, _dashboardId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(dashboardsKey, context.previous);
      }
    },
  });

  async function createDashboard(dashboard: CreateDashboardRequest): Promise<DashboardModel> {
    return createDashboardMutation.mutateAsync(dashboard);
  }

  async function deleteDashboard(dashboardId: string): Promise<void> {
    await deleteDashboardMutation.mutateAsync(dashboardId);
  }

  // Loading: true only on initial load (no cached data yet)
  const loading =
    orgQuery.isPending ||
    (activeResources.threads && threadsQuery.isPending) ||
    (activeResources.projects && projectsQuery.isPending) ||
    (activeResources.brains && brainsQuery.isPending) ||
    (activeResources.members && membersQuery.isPending) ||
    (activeResources.metrics && metricsQuery.isPending) ||
    (activeResources.datasources && datasourcesQuery.isPending) ||
    (activeResources.dashboards && dashboardsQuery.isPending);

  // Refreshing: true during background refetches (data already available)
  const isRefreshing =
    orgQuery.isFetching ||
    (activeResources.threads && threadsQuery.isFetching) ||
    (activeResources.projects && projectsQuery.isFetching) ||
    (activeResources.brains && brainsQuery.isFetching) ||
    (activeResources.members && membersQuery.isFetching) ||
    (activeResources.metrics && metricsQuery.isFetching) ||
    (activeResources.datasources && datasourcesQuery.isFetching) ||
    (activeResources.dashboards && dashboardsQuery.isFetching);

  // Collect all errors for granular debugging
  const errors = [
    orgQuery.error,
    activeResources.threads ? threadsQuery.error : null,
    activeResources.projects ? projectsQuery.error : null,
    activeResources.brains ? brainsQuery.error : null,
    activeResources.members ? membersQuery.error : null,
    activeResources.metrics ? metricsQuery.error : null,
    activeResources.datasources ? datasourcesQuery.error : null,
    activeResources.dashboards ? dashboardsQuery.error : null,
  ].filter(Boolean) as Error[];

  const error = errors[0] || null;

  const isError =
    orgQuery.isError ||
    (activeResources.threads && threadsQuery.isError) ||
    (activeResources.projects && projectsQuery.isError) ||
    (activeResources.brains && brainsQuery.isError) ||
    (activeResources.members && membersQuery.isError) ||
    (activeResources.metrics && metricsQuery.isError) ||
    (activeResources.datasources && datasourcesQuery.isError) ||
    (activeResources.dashboards && dashboardsQuery.isError);

  return (
    <OrgContext.Provider
      value={{
        threads: threadsQuery.data || [],
        projects: projectsQuery.data || [],
        brains: brainsQuery.data || [],
        org: orgQuery.data || null,
        fetchOrgData,
        loading,
        isRefreshing,
        error,
        members: membersQuery.data || [],
        metrics: metricsQuery.data || [],
        datasources: datasourcesQuery.data || [],
        dashboards: dashboardsQuery.data || [],
        createDashboard,
        deleteDashboard,
        retry,
        isError,
      }}
    >
      {children}
    </OrgContext.Provider>
  );
}

export const useOrgContext = (): OrgContextType => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error("useOrgContext must be used within an OrgProvider");
  }
  return context;
};
